/**
 * Expense Claims Approval Queue — for HR / Finance / Director.
 * Shows pending, approved, and rejected claims; lets admins approve/reject.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Receipt, CheckCircle, XCircle, Clock, AlertCircle, Loader, Filter
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
};

const StatCard = ({ label, value, color, icon: Icon }) => (
  <div className={`bg-white rounded-xl p-4 border border-gray-100 shadow-sm border-l-4 border-l-${color}-500`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className={`text-2xl font-bold text-${color}-600 mt-1`}>{value}</p>
      </div>
      {Icon && <Icon className={`w-8 h-8 text-${color}-400`} />}
    </div>
  </div>
);

const ExpenseClaimsQueuePage = () => {
  const [claims, setClaims] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [actingId, setActingId] = useState('');

  const fetchClaims = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? {} : { status: filter };
      const { data } = await axios.get(`${API}/expense-claims`, { params });
      setClaims(data.claims || []);
      setSummary(data.summary || null);
    } catch (e) {
      toast.error('Gagal memuat tuntutan');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchClaims(); }, [fetchClaims]);

  const handleApprove = async (claim) => {
    if (!window.confirm(`Approve tuntutan RM${claim.amount.toFixed(2)} dari ${claim.staff_name}?`)) return;
    setActingId(claim.id);
    try {
      await axios.post(`${API}/expense-claims/${claim.id}/approve`, { note: '' });
      toast.success('Approved! Auto-posted ke Expenses.');
      fetchClaims();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal approve');
    } finally { setActingId(''); }
  };

  const handleReject = async (claim) => {
    const reason = window.prompt('Sebab reject?');
    if (!reason || !reason.trim()) return;
    setActingId(claim.id);
    try {
      await axios.post(`${API}/expense-claims/${claim.id}/reject`, { reason });
      toast.success('Rejected.');
      fetchClaims();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal reject');
    } finally { setActingId(''); }
  };

  return (
    <div className="space-y-6" data-testid="expense-claims-queue">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Receipt className="w-6 h-6 text-indigo-600" /> Tuntutan Belanja
          </h1>
          <p className="text-gray-500 text-sm">Review & approve tuntutan dari staff (auto-post ke Expenses)</p>
        </div>
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg p-1">
          {[
            { id: 'pending', label: 'Pending' },
            { id: 'approved', label: 'Approved' },
            { id: 'rejected', label: 'Rejected' },
            { id: 'all', label: 'Semua' },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`filter-${f.id}`}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                filter === f.id ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label="Pending" value={summary.pending} color="amber" icon={Clock} />
          <StatCard label="Approved" value={summary.approved} color="emerald" icon={CheckCircle} />
          <StatCard label="Rejected" value={summary.rejected} color="red" icon={XCircle} />
          <StatCard
            label="Total Pending (RM)"
            value={`RM${(summary.total_pending_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            color="indigo"
            icon={Receipt}
          />
        </div>
      )}

      {/* Claims List */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center text-gray-400">
            <Loader className="w-6 h-6 animate-spin" />
          </div>
        ) : claims.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <AlertCircle className="w-10 h-10 mx-auto mb-2" />
            <p className="text-sm">Tiada tuntutan dalam kategori ini.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {claims.map((c) => (
              <div key={c.id} className="p-4 sm:p-5 hover:bg-gray-50 transition-colors" data-testid={`claim-row-${c.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-semibold text-gray-800 truncate">{c.staff_name || c.staff_id}</span>
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${STATUS_BADGE[c.status] || 'bg-gray-100 text-gray-700'}`}>
                        {c.status}
                      </span>
                      <span className="text-xs text-gray-400">{c.category}</span>
                    </div>
                    <p className="text-xl font-bold text-gray-800">
                      RM{(c.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Tarikh: {c.date} • Submitted: {(c.submitted_at || '').slice(0, 10)}
                    </p>
                    {c.description && <p className="text-xs text-gray-600 mt-1 line-clamp-2">{c.description}</p>}
                    {c.status === 'rejected' && c.rejected_reason && (
                      <p className="text-xs text-red-600 mt-1">Sebab reject: {c.rejected_reason}</p>
                    )}
                    {c.status === 'approved' && c.approved_by && (
                      <p className="text-xs text-emerald-600 mt-1">Diluluskan oleh: {c.approved_by}</p>
                    )}
                  </div>
                  {c.status === 'pending' && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleApprove(c)}
                        disabled={actingId === c.id}
                        data-testid={`approve-${c.id}`}
                        className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 text-sm font-medium flex items-center gap-1"
                      >
                        <CheckCircle className="w-4 h-4" /> Approve
                      </button>
                      <button
                        onClick={() => handleReject(c)}
                        disabled={actingId === c.id}
                        data-testid={`reject-${c.id}`}
                        className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 disabled:opacity-50 text-sm font-medium flex items-center gap-1"
                      >
                        <XCircle className="w-4 h-4" /> Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpenseClaimsQueuePage;
